import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import * as Icons from "lucide-react";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

const fmt12 = (t) => {
  if (!t || !/^\d{1,2}:\d{2}/.test(t)) return t;
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ap}`;
};
const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); } catch { return d; } };
const todayStr = () => new Date().toISOString().slice(0, 10);

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" style={{ fontFamily: "'Segoe UI',Roboto,Helvetica,Arial,sans-serif" }}>
    <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden" data-testid="rebook-card">
      <div className="h-2" style={{ background: "linear-gradient(90deg,#059669,#10b981)" }} />
      <div className="p-7 sm:p-9">{children}</div>
    </div>
  </div>
);

export default function RebookConfirm() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(sp.get("date") && sp.get("time") ? { date: sp.get("date"), time: sp.get("time") } : null);
  const [browse, setBrowse] = useState(false);
  const [pickDate, setPickDate] = useState(sp.get("date") || todayStr());
  const [slots, setSlots] = useState([]);
  const [slotsBusy, setSlotsBusy] = useState(false);
  const [booking, setBooking] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/scheduling/rebook/${token}`); setInfo(data); }
    catch (e) { setError(e.response?.data?.detail || "This reschedule link is invalid."); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const loadSlots = useCallback(async (d) => {
    setSlotsBusy(true); setSlots([]);
    try { const { data } = await api.get(`/scheduling/rebook/${token}/slots`, { params: { date: d } }); setSlots(data.slots || []); }
    catch { setSlots([]); }
    finally { setSlotsBusy(false); }
  }, [token]);
  useEffect(() => { if (browse) loadSlots(pickDate); }, [browse, pickDate, loadSlots]);

  const confirm = async () => {
    if (!sel || booking) return;
    setBooking(true); setError(null);
    try {
      const { data } = await api.post(`/scheduling/rebook/${token}`, { date: sel.date, start_time: sel.time });
      setDone(data);
    } catch (e) { setError(e.response?.data?.detail || "We couldn't book that time. Please pick another."); if (String(e.response?.status) === "409") { setSel(null); setBrowse(true); } }
    finally { setBooking(false); }
  };

  if (loading) return <Shell><div className="py-10 text-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-emerald-500 inline" /></div></Shell>;

  if (error && !info) return <Shell><div className="text-center py-4" data-testid="rebook-error"><Icons.AlertCircle className="h-10 w-10 text-rose-400 mx-auto mb-3" /><h1 className="text-xl font-bold text-slate-800 mb-1">Link not available</h1><p className="text-slate-500 text-sm">{error}</p></div></Shell>;

  if (done || info?.used) return (
    <Shell>
      <div className="text-center py-2" data-testid="rebook-success">
        <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4"><Icons.CalendarCheck className="h-7 w-7 text-emerald-600" /></div>
        <h1 className="text-2xl font-bold text-slate-800 mb-2">{done ? "You're all set!" : "Already rebooked"}</h1>
        <p className="text-slate-500 mb-5">{done ? <>Your {info?.meeting_type_name} is confirmed for <span className="font-semibold text-slate-700">{done.when}</span>.</> : "This appointment has already been rescheduled. Check your email for the confirmation."}</p>
        {done?.join_url && <a href={done.join_url} target="_blank" rel="noreferrer" data-testid="rebook-join-link" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl px-6 py-3"><Icons.Video className="h-5 w-5" />Join your video meeting</a>}
        <p className="text-xs text-slate-400 mt-5">A confirmation email is on its way. See you soon!</p>
      </div>
    </Shell>
  );

  if (info?.expired) return <Shell><div className="text-center py-4"><Icons.Clock className="h-10 w-10 text-amber-400 mx-auto mb-3" /><h1 className="text-xl font-bold text-slate-800 mb-1">This link has expired</h1><p className="text-slate-500 text-sm">Please reply to our email or contact us and we'll help you rebook right away.</p></div></Shell>;

  return (
    <Shell>
      <div className="text-center mb-6">
        <div className="h-12 w-12 rounded-xl bg-emerald-50 flex items-center justify-center mx-auto mb-3"><Icons.CalendarClock className="h-6 w-6 text-emerald-600" /></div>
        <h1 className="text-2xl font-bold text-slate-800">Pick a new time</h1>
        <p className="text-slate-500 text-sm mt-1">Hi {info?.contact_name || "there"} — let's get your <span className="font-medium text-slate-700">{info?.meeting_type_name}</span>{info?.staff_name ? <> with {info.staff_name}</> : null} rescheduled.</p>
      </div>

      {error && <div className="mb-4 text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="rebook-inline-error">{error}</div>}

      {sel && !browse && (
        <div className="mb-4" data-testid="rebook-selected">
          <div className="rounded-xl border-2 border-emerald-500 bg-emerald-50 px-4 py-3 mb-3">
            <div className="text-xs text-emerald-600 font-semibold uppercase tracking-wide">Selected time</div>
            <div className="text-lg font-bold text-slate-800">{fmtDate(sel.date)}</div>
            <div className="text-slate-600">{fmt12(sel.time)} ({info?.timezone}) · {info?.duration_min} min</div>
          </div>
          <button onClick={confirm} disabled={booking} data-testid="rebook-confirm-btn" className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl px-6 py-3.5 disabled:opacity-60">
            {booking ? <Icons.Loader2 className="h-5 w-5 animate-spin" /> : <Icons.Check className="h-5 w-5" />}Confirm this time
          </button>
          <button onClick={() => { setBrowse(true); setSel(null); }} data-testid="rebook-browse-btn" className="w-full text-sm text-slate-500 hover:text-slate-700 mt-3">Choose a different time</button>
        </div>
      )}

      {!sel && !browse && (
        <div data-testid="rebook-suggestions">
          {(info?.suggested || []).length > 0 ? (
            <>
              <div className="text-sm font-medium text-slate-600 mb-2">Our next available times — tap to select:</div>
              <div className="space-y-2">
                {info.suggested.map((s) => (
                  <button key={`${s.date}-${s.time}`} onClick={() => setSel({ date: s.date, time: s.time })} data-testid={`rebook-suggest-${s.date}-${s.time}`}
                    className="w-full flex items-center justify-between border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 rounded-xl px-4 py-3 text-left transition-colors">
                    <span><span className="font-semibold text-slate-800">{fmtDate(s.date)}</span><span className="text-slate-500"> · {fmt12(s.time)}</span></span>
                    <Icons.ChevronRight className="h-4 w-4 text-slate-400" />
                  </button>
                ))}
              </div>
            </>
          ) : <p className="text-sm text-slate-500 mb-2">Let's find a time that works — browse the calendar below.</p>}
          <button onClick={() => setBrowse(true)} data-testid="rebook-more-times-btn" className="w-full text-sm text-emerald-600 hover:text-emerald-700 font-medium mt-4">See more times →</button>
        </div>
      )}

      {browse && (
        <div data-testid="rebook-browse">
          <label className="text-sm font-medium text-slate-600 mb-1 block">Choose a date</label>
          <input type="date" value={pickDate} min={todayStr()} onChange={(e) => setPickDate(e.target.value)} data-testid="rebook-date-input"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500" />
          {slotsBusy ? <div className="py-6 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-emerald-500 inline" /></div>
            : slots.length === 0 ? <p className="text-sm text-slate-400 py-3">No open times on this date. Try another day.</p>
            : <div className="grid grid-cols-3 gap-2">
                {slots.map((s) => (
                  <button key={s} onClick={() => { setSel({ date: pickDate, time: s }); setBrowse(false); }} data-testid={`rebook-slot-${s}`}
                    className="border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 rounded-lg px-2 py-2 text-sm text-slate-700">{fmt12(s)}</button>
                ))}
              </div>}
          <button onClick={() => setBrowse(false)} className="w-full text-sm text-slate-500 hover:text-slate-700 mt-4">← Back</button>
        </div>
      )}
    </Shell>
  );
}
